/**
 * Cost coverage — which billed lines carry a cost, on whose basis, and what is left over.
 *
 * WHY IT IS HERE. The cards below show billed value beside a cost, and any reader fluent
 * enough to subtract gets the wrong answer — GM is taken on the costed value, which is the
 * part of the book that carries a cost. That was true and invisible: one 9.5px note. The band
 * puts the whole £ split on the page, so the four tiles below visibly balance.
 *
 * Modelled on the Capacity surface's routing-confidence band (components/loading/
 * ConfidencePanel) and doing the same job one surface over: grade the input before the
 * headline, in the reader's eye-line, rather than defend it afterwards.
 *
 * ⭐ THE ENTITY TILES ARE STATEMENTS, NOT A CONTROL (24 Aug 2026). They were buttons — the
 * cost-basis toggle, wearing the band's clothes — and pressing one switched the cards beneath.
 * That control never had anything to choose: `cost_basis` does not vary inside a site, so it
 * did nothing at Kirkstall, nothing at Oracle, and at Group it showed one entity's cost while
 * hiding the other's. The server now serves one cost; these tiles say what it is made of.
 *
 * ⚠ COVERAGE, NOT BASIS, IS WHAT LIMITS THE BLEND NOW. Kirkstall prices 96.8% of its own
 * billed value and Oracle 66.1%, and Oracle's uncosted third sits mostly in the one
 * material-heavy account it runs — so the blended margin is biased HIGH. Each tile prints its
 * own share, and the residue tile prints what neither reaches, so the bias is on the page.
 */

import React from 'react'
import { formatCurrency } from '../utils/formatters'
import MarginReconcile from './MarginReconcile'
import type { Coverage, CoverageLens } from '../viewmodels/marginCoverage'
import type { SageWindow } from '../viewmodels/marginSage'
import type { BreakdownTiles } from '../viewmodels/orderBook'

const gbp = (v: number): string => formatCurrency(v, '£', 0)
const num = (v: number): string => v.toLocaleString('en-GB')
const pct = (v: number): string => `${v.toFixed(v < 10 ? 1 : 0)}%`

const TILE = 'flex flex-col gap-0.5 rounded-[9px] border border-line bg-panel px-[11px] py-[9px] text-left'
const CAP = 'font-mono text-[9px] font-semibold uppercase tracking-[.09em] text-mut2'
const FIG = 'font-sans text-[16px] font-semibold'
const NOTE = 'font-sans text-[10px] leading-[1.35] text-mut2'

function Tile({ label, value, note, tone }: {
  label: string
  value: string
  note: React.ReactNode
  tone?: string
}): React.JSX.Element {
  return (
    <div className={TILE}>
      <div className={CAP}>{label}</div>
      <div className={`${FIG} ${tone ?? 'text-ink'}`}>{value}</div>
      <div className={NOTE}>{note}</div>
    </div>
  )
}

/** One entity's slice of the one cost: how much of the node it prices, what that cost is,
 * and what KIND of number it is. The kind is the whole reason this tile exists — a measured
 * cost and an estimate may be added and must still be told apart. */
function LensTile({ lens, only }: { lens: CoverageLens; only: boolean }): React.JSX.Element {
  return (
    <Tile
      // ⚠ At a single entity the page IS that entity, so its name says nothing — the tile has
      // to say what KIND of cost this is instead. At Group the name is the whole point.
      label={only ? 'Costed value' : lens.entity}
      value={gbp(lens.costedValue)}
      note={
        <>
          {pct(lens.pct)} of value · {num(lens.costedLines)} lines
          <br />
          <span className="text-mut2">
            {lens.gmPct === null ? lens.kind : `GM ${gbp(lens.gm)} · ${lens.gmPct.toFixed(1)}% — ${lens.kind}`}
          </span>
        </>
      }
    />
  )
}

/** The one thing the table cannot state: which of the two GM percentages is the truth.
 * Neither is — they bracket it, and saying so is more use than picking one. */
function Bracket({ c, erp }: { c: Coverage; erp: string }): React.JSX.Element {
  return (
    <p className="max-w-[980px] pt-2.5 font-sans text-[11.5px] leading-[1.55] text-mut">
      The two margin percentages <span className="font-semibold text-ink">bracket the answer</span>
      {' '}rather than disagree: the lower one assumes the {gbp(c.uncosted)} we cannot cost earned
      nothing, the higher one assumes it earned what the rest did. We cannot say which — that is
      why those lines are held out. And the cards are the {erp} invoice while Sage is the ledger,
      which holds every account the business posts and not only billed sales, so the two agree on
      revenue and are read carefully on cost.
      {c.lenses.length > 1 && (
        <>
          {' '}The two entities are added, and they are{' '}
          <span className="font-semibold text-ink">not the same kind of number</span>: one is what
          the job took, the other what the quote said it would take, priced at a direct rate.
        </>
      )}
    </p>
  )
}

function Heading(): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 pb-2.5 pt-3">
      <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[.09em] text-mut2">
        Cost coverage
      </span>
      <span className="font-mono text-[10.5px] text-mut2">
        states the population and the basis behind every part of it
      </span>
    </div>
  )
}

/**
 * ⭐ COLLAPSED BY DEFAULT FROM 24 Aug 2026. This band sat ABOVE the cards, and the amount it
 * held out — the reason Value − Cost did not equal Gross Margin — was one tile inside it. The
 * cards now carry that amount themselves and subtract correctly, so the band stopped being
 * the fix for a broken row and went back to being what it is: the explanation behind one.
 * It is still one click away, and nothing in it was removed.
 */
export default function MarginCoverage({ coverage, erp, tiles, sage }: {
  coverage: Coverage
  /** The system the billed value came from — FactoryMaster, Red Ant, or both at Group. */
  erp: string
  /** The card strip's own figures, so the reconciliation can mirror it row for row. */
  tiles: BreakdownTiles
  sage: SageWindow
}): React.JSX.Element {
  const c = coverage
  return (
    <details className="rounded-xl border border-line bg-panel2 px-[18px] py-2.5">
      <summary className="cursor-pointer select-none font-mono text-[9.5px] font-semibold uppercase tracking-[.09em] text-mut2">
        Why these numbers differ · reconcile the cards, and compare them to Sage
      </summary>
      <MarginReconcile tiles={tiles} uncosted={c.uncosted} uncostedLines={c.uncostedLines} sage={sage} />
      <Bracket c={c} erp={erp} />
      <Heading />
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <Tile
          label={`Billed value · ${erp}`}
          value={gbp(c.value)}
          note={<>{num(c.lineCount)} lines · the invoice, not the ledger · the cards&apos; top figure</>}
        />
        {c.lenses.map((lens) => (
          <LensTile key={lens.entity} lens={lens} only={c.lenses.length === 1} />
        ))}
        <Tile
          label="No cost basis"
          value={gbp(c.uncosted)}
          tone={c.uncosted > 0 ? 'text-amber' : 'text-ink'}
          note={
            c.uncosted > 0
              ? `${pct(c.uncostedPct)} of value · ${num(c.uncostedLines)} lines · no usable job or quote`
              : 'every billed line is costed'
          }
        />
      </div>
    </details>
  )
}
